import { CreateStreakRequest, CreateStreakRequestV2, METRICS_EVENTS } from "@cede/types";
import { isServerApiError, isServerApiRfcError, QueryKeys } from "@cede/utils";
import { invalidateEpochLimitQuery } from "./useAutomaticStreakEpochLimit";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { useHubUser } from "@/hooks/user/useHubUser";
import { createAutomaticStreak, createAutomaticStreakV2 } from "@/utils/api";
import { extractErrorCode } from "@/utils/api/is4xxHttpError";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";

const EPOCH_LIMIT_REACHED_ERROR_CODE = "AUTOMATIC_STREAK_EPOCH_LIMIT_REACHED";

export interface UseCreateStreakOptions {
  version?: 1 | 2;
  campaignName?: string;
  onSuccess?: (streakId: string) => void;
  onError?: (error: string) => void;
}

type CreateStreakRequestByVersion<V extends 1 | 2> = V extends 2 ? CreateStreakRequestV2 : CreateStreakRequest;

export const useCreateStreak = <V extends 1 | 2 = 1>(options?: UseCreateStreakOptions & { version?: V }) => {
  const { auth } = useHubUser();
  const queryClient = useQueryClient();
  const { trackEvent } = useMetricsEvents();
  const { enqueueSnackbar } = useSnackbar();

  const version = options?.version ?? 1;

  const mutation = useMutation({
    mutationFn: async (request: CreateStreakRequestByVersion<V>) => {
      if (version === 2) {
        const result = await createAutomaticStreakV2(auth, request as CreateStreakRequestV2);

        if (isServerApiError(result) || isServerApiRfcError(result)) {
          const errorMessage = isServerApiRfcError(result) ? result.detail : result.message;
          throw new Error(errorMessage);
        }

        return result;
      }

      const result = await createAutomaticStreak(auth, request as CreateStreakRequest);

      if (isServerApiError(result) || isServerApiRfcError(result)) {
        const errorMessage = isServerApiRfcError(result) ? result.detail : result.message;
        throw new Error(errorMessage);
      }

      return result;
    },
    onSuccess: (data, variables) => {
      if (version === 2) {
        const v2Variables = variables as CreateStreakRequestV2;
        trackEvent(METRICS_EVENTS.AUTOMATIC_STREAK_CREATED, {
          streak_id: data.streakId,
          campaign_name: v2Variables.campaignName,
          epoch_id: v2Variables.epochId,
          next_epoch_id: v2Variables.nextEpochId,
          total_target_volume: v2Variables.totalTargetVolume,
          version: "V2",
        });
      } else {
        const v1Variables = variables as CreateStreakRequest;
        trackEvent(METRICS_EVENTS.AUTOMATIC_STREAK_CREATED, {
          streak_id: data.streakId,
          campaign_name: v1Variables.campaignName,
          epoch_id: v1Variables.epochId,
          total_trades: v1Variables.totalTrades,
          volume_per_cycle: v1Variables.volumePerCycle,
          interval_seconds: v1Variables.intervalSeconds,
          also_run_for_next_epoch: v1Variables.alsoRunForNextEpoch || false,
        });
      }

      // Invalidate user streaks query to refresh the list
      queryClient.invalidateQueries({ queryKey: [QueryKeys.AUTOMATIC_STREAKS] });

      enqueueSnackbar("Autopilot started successfully!", {
        variant: "success",
      });

      options?.onSuccess?.(data.streakId);
    },
    onError: (error: Error) => {
      // Invalidate epoch limit cache when the limit is reached so the UI updates immediately
      const errorCode = extractErrorCode(error);
      if (errorCode === EPOCH_LIMIT_REACHED_ERROR_CODE && options?.campaignName) {
        invalidateEpochLimitQuery(queryClient, options.campaignName);
      }

      enqueueSnackbar(`Failed to start autopilot: ${error.message}`, {
        variant: "error",
      });

      options?.onError?.(error.message);
    },
  });

  return {
    createStreak: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error?.message,
  };
};
